"use client"

import * as React from "react"
import { ArrowDownIcon, ArrowLeftRightIcon, ArrowUpIcon, PaperclipIcon } from "lucide-react"

import {
  FinanceInvestigationFlow,
  financeInvestigationFlow,
  type FinanceInvestigationAggregate,
  type FinanceInvestigationEvent,
} from "@ibada/shared-types/finance"
import { Skeleton, Tooltip, TooltipContent, TooltipTrigger, cn } from "@ibada/ui"

import {
  formatSignedInvestigationAmount,
  formatSignedTotal,
  formatZonedDayHeading,
  formatZonedTime,
  groupInvestigationEventsByDay,
  isCrossedOutInvestigationEvent,
} from "@/features/finance/lib/finance-investigation-explorer"
import { PersonPreviewCell } from "@/shared/components/person-preview"
import { useTranslations } from "@/i18n"

type FormatMoney = (amount: number) => string

const CARD_CLASS = "rounded-2xl border border-border/70 bg-card"

/** The four figures over the whole result set (I1): movements, money in, money out, net. */
export function ExplorerFigures({
  aggregate,
  formatMoney,
  hasPeriod,
  loading,
}: {
  aggregate?: FinanceInvestigationAggregate
  formatMoney: FormatMoney
  hasPeriod: boolean
  loading: boolean
}) {
  const t = useTranslations("finance.audit.explorer.figures")
  const net = aggregate?.netAmount ?? 0
  const figures = [
    {
      id: "movements",
      label: t("movements"),
      value: String(aggregate?.count ?? 0),
      hint: hasPeriod ? t("movementsHintPeriod") : t("movementsHintAll"),
      tone: "",
    },
    {
      id: "inflow",
      label: t("inflow"),
      value: formatSignedTotal(aggregate?.inflowAmount ?? 0, formatMoney),
      hint: t((aggregate?.inflowCount ?? 0) === 1 ? "inflowHintOne" : "inflowHint", { count: aggregate?.inflowCount ?? 0 }),
      tone: (aggregate?.inflowAmount ?? 0) > 0 ? "text-teal-700 dark:text-teal-400" : "",
    },
    {
      id: "outflow",
      label: t("outflow"),
      value: formatSignedTotal(-(aggregate?.outflowAmount ?? 0), formatMoney),
      hint: t((aggregate?.outflowCount ?? 0) === 1 ? "outflowHintOne" : "outflowHint", { count: aggregate?.outflowCount ?? 0 }),
      tone: "",
    },
    {
      id: "net",
      label: t("net"),
      value: formatSignedTotal(net, formatMoney),
      hint: t("netHint"),
      tone: net > 0 ? "text-teal-700 dark:text-teal-400" : net < 0 ? "text-rose-700 dark:text-rose-400" : "",
    },
  ]
  return (
    <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
      {figures.map((figure) => (
        <div key={figure.id} className={cn(CARD_CLASS, "flex min-w-0 flex-col px-4 py-3")} data-testid={`explorer-figure-${figure.id}`}>
          <span className='text-[12.5px] font-bold text-muted-foreground'>{figure.label}</span>
          {loading ? (
            <Skeleton className='my-1 h-6 w-24 rounded-full' />
          ) : (
            <span className={cn("truncate text-[22px] leading-tight font-extrabold tabular-nums", figure.tone)}>{figure.value}</span>
          )}
          <span className='truncate text-xs text-muted-foreground'>{figure.hint}</span>
        </div>
      ))}
    </div>
  )
}

const FLOW_ICON: Record<FinanceInvestigationFlow, { icon: typeof ArrowDownIcon; className: string }> = {
  [FinanceInvestigationFlow.In]: { icon: ArrowDownIcon, className: "bg-teal-100 text-teal-800 dark:bg-teal-500/15 dark:text-teal-300" },
  [FinanceInvestigationFlow.Out]: { icon: ArrowUpIcon, className: "bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300" },
  [FinanceInvestigationFlow.Internal]: { icon: ArrowLeftRightIcon, className: "bg-muted text-muted-foreground" },
}

/** The account the money went through, by name; the journal accounts with their codes on hover. */
function AccountName({ event }: { event: FinanceInvestigationEvent }) {
  const t = useTranslations("finance.audit.explorer.row")
  const name = event.moneyAccountName ?? event.accounts[0]?.name ?? null
  if (!name) return null
  if (event.accounts.length === 0) return <span className='truncate'>{name}</span>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='truncate'>{name}</span>
      </TooltipTrigger>
      <TooltipContent>
        <span className='flex flex-col gap-0.5'>
          <span className='font-bold'>{t("journalAccounts")}</span>
          {event.accounts.map((account) => (
            <span key={`${account.accountingAccountId}-${account.direction}`}>
              <span className='font-mono'>{account.code}</span> {account.name}
            </span>
          ))}
        </span>
      </TooltipContent>
    </Tooltip>
  )
}

function MovementRow({
  event,
  formatMoney,
  timeZone,
  locale,
  onOpen,
}: {
  event: FinanceInvestigationEvent
  formatMoney: FormatMoney
  timeZone: string
  locale: string
  onOpen: (transactionId: string) => void
}) {
  const t = useTranslations("finance.audit")
  const flow = financeInvestigationFlow(event.type)
  const { icon: Icon, className: iconClass } = FLOW_ICON[flow]
  const typeLabel = t(`type.${event.type}` as never)
  const title = event.description ?? typeLabel
  const crossedOut = isCrossedOutInvestigationEvent(event)

  return (
    <li>
      <button
        type='button'
        onClick={() => onOpen(event.transactionId)}
        className='flex w-full items-center gap-3 px-4 py-2.5 text-left outline-none hover:bg-muted/50 focus-visible:bg-muted/60'
        data-testid='explorer-movement'
      >
        <span aria-hidden className={cn("flex size-[34px] shrink-0 items-center justify-center rounded-full", iconClass)}>
          <Icon className='size-[15px]' strokeWidth={2} />
        </span>
        <span className='flex min-w-0 flex-1 flex-col'>
          <span className={cn("truncate text-[13.5px] font-bold", crossedOut && "text-muted-foreground line-through")}>{title}</span>
          <span className='flex min-w-0 items-center gap-1 text-[12.5px] text-muted-foreground'>
            <span className='shrink-0'>{typeLabel}</span>
            {event.reference ? (
              <>
                <span aria-hidden>·</span>
                <span className='shrink-0 font-mono'>{event.reference}</span>
              </>
            ) : null}
            {event.moneyAccountName || event.accounts.length > 0 ? (
              <>
                <span aria-hidden>·</span>
                <AccountName event={event} />
              </>
            ) : null}
            {event.evidence.present ? (
              <>
                <span aria-hidden>·</span>
                <span className='inline-flex shrink-0 items-center gap-1'>
                  <PaperclipIcon className='size-[11px]' aria-hidden />
                  {event.evidence.receiptNumber && event.evidence.hasReceipt
                    ? t("explorer.row.receipt", { number: event.evidence.receiptNumber })
                    : t("explorer.row.evidence")}
                </span>
              </>
            ) : null}
          </span>
        </span>
        <span className='hidden w-40 shrink-0 text-[12.5px] text-muted-foreground md:flex'>
          {event.actorName ? (
            <PersonPreviewCell
              identity={{ userId: event.actorUserId }}
              name={event.actorName}
              avatarUrl={event.actorAvatarUrl}
              enablePreview={false}
            />
          ) : null}
        </span>
        <span
          className={cn(
            "w-32 shrink-0 text-right text-sm font-bold whitespace-nowrap tabular-nums",
            flow === FinanceInvestigationFlow.In ? "text-teal-700 dark:text-teal-400" : "text-foreground",
            crossedOut && "text-muted-foreground line-through",
          )}
        >
          {formatSignedInvestigationAmount(event, formatMoney)}
        </span>
        <span className='w-11 shrink-0 text-right text-[12.5px] text-muted-foreground tabular-nums'>
          {formatZonedTime(event.occurredAt, timeZone, locale)}
        </span>
      </button>
    </li>
  )
}

/** The page's movements grouped by day in the workspace zone, each day a soft card with its count. */
export function ExplorerMovementsByDay({
  events,
  formatMoney,
  timeZone,
  locale,
  onOpen,
}: {
  events: readonly FinanceInvestigationEvent[]
  formatMoney: FormatMoney
  timeZone: string
  locale: string
  onOpen: (transactionId: string) => void
}) {
  const t = useTranslations("finance.audit.explorer")
  const groups = React.useMemo(() => groupInvestigationEventsByDay(events, timeZone), [events, timeZone])
  return (
    <div className='flex flex-col gap-4'>
      {groups.map((group) => (
        <section key={group.dayKey} aria-label={formatZonedDayHeading(group.sample, timeZone, locale)}>
          <div className='flex items-baseline gap-2 pl-1'>
            <h3 className='text-[13.5px] font-bold'>{formatZonedDayHeading(group.sample, timeZone, locale)}</h3>
            <span className='text-[12.5px] text-muted-foreground'>{t(group.events.length === 1 ? "dayCountOne" : "dayCount", { count: group.events.length })}</span>
          </div>
          <ul className={cn(CARD_CLASS, "mt-1.5 divide-y divide-border/60 overflow-hidden")}>
            {group.events.map((event) => (
              <MovementRow
                key={event.transactionId}
                event={event}
                formatMoney={formatMoney}
                timeZone={timeZone}
                locale={locale}
                onOpen={onOpen}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

/** Loading placeholder in the shape of the day list. */
export function ExplorerMovementsSkeleton() {
  return (
    <div className={cn(CARD_CLASS, "divide-y divide-border/60")}>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className='flex items-center gap-3 px-4 py-3'>
          <Skeleton className='size-[34px] rounded-full' />
          <div className='flex flex-1 flex-col gap-1.5'>
            <Skeleton className='h-3.5 w-1/3 rounded-full' />
            <Skeleton className='h-3 w-1/2 rounded-full' />
          </div>
          <Skeleton className='h-4 w-20 rounded-full' />
        </div>
      ))}
    </div>
  )
}
